/**
 * XCS File Generator for xTool Creative Space
 *
 * Generates XCS format files that can be opened in xTool laser software.
 * XCS is a JSON-based format containing canvas shapes and device processing settings.
 */

import * as opentype from 'opentype.js';
import { TemplateShape, ShapeProcessSettings } from '../types/dbTypes';
import { Font } from '../types/dbTypes';
import { ExtendedField, parseFontFamily, DEFAULT_FONT_FAMILY } from './svgGenerator';
import { calculateFontSizeForBox, Font as TextGenFont, loadFont, pathToSvg, findFontByName } from './textGenerator';
import { ROBOTO_REGULAR_BASE64 } from '../utils/robotoFont';
import { parsePathCommands, type PathCommand } from '../utils/pathUtils';

// ============================================================
// XTOOL XCS LAYER COLORS
// ============================================================

// Legacy xTool color exports for backwards compatibility
export const XCS_LAYER_BLUE = '#0000FF';
export const XCS_LAYER_RED = '#FF0000';
export const XCS_LAYER_BLACK = '#000000';
export const XCS_LAYER_CYAN = '#00FFFF';
export const XCS_LAYER_ORANGE = '#FFA500';
export const XCS_LAYER_YELLOW = '#FFFF00';
export const XCS_LAYER_GREEN = '#00FF00';
export const XCS_LAYER_PURPLE = '#800080';

// xTool XCS layer colors (smaller palette)
export const XTOOL_LAYER_COLORS = [
    { value: '#0000FF', label: 'Blue' },
    { value: '#FF0000', label: 'Red' },
    { value: '#000000', label: 'Black' },
    { value: '#00FFFF', label: 'Cyan' },
    { value: '#FFA500', label: 'Orange' },
    { value: '#FFFF00', label: 'Yellow' },
    { value: '#00FF00', label: 'Green' },
    { value: '#800080', label: 'Purple' },
] as const;

// ============================================================
// LASER TYPE CONFIGURATIONS
// ============================================================

// Light source modes supported by xTool lasers
export type LightSourceMode = 'blue' | 'red' | 'fiber' | 'uv';

export interface LaserConfig {
    extId: string;
    extName: string;
    hasLightSourceMode: boolean;
    lightSources?: LightSourceMode[];  // Available light sources for this laser
    defaultLightSource?: LightSourceMode;
}

export const LASER_CONFIGS: Record<string, LaserConfig> = {
    // Original F1 - 10W Diode + 2W IR
    'f1': { extId: 'F1', extName: 'F1', hasLightSourceMode: true, lightSources: ['blue', 'red'], defaultLightSource: 'blue' },
    // F1 Ultra - 20W Diode + Fiber
    'f1ultra': { extId: 'F1Ultra', extName: 'F1 Ultra', hasLightSourceMode: true, lightSources: ['blue', 'fiber'], defaultLightSource: 'blue' },
    // F2 Ultra (Dual) - 20W Diode + Fiber
    'f2ultra': { extId: 'GS004-CLASS-4', extName: 'F2 Ultra', hasLightSourceMode: true, lightSources: ['blue', 'fiber'], defaultLightSource: 'blue' },
    // F2 Ultra (Single) - 20W Diode only
    'f2ultrasingle': { extId: 'GS007-CLASS-4', extName: 'F2 Ultra (Single)', hasLightSourceMode: true, lightSources: ['blue'], defaultLightSource: 'blue' },
    // F2 Ultra UV - UV laser only
    'f2ultrauv': { extId: 'GS009-CLASS-4', extName: 'F2 Ultra UV', hasLightSourceMode: true, lightSources: ['uv'], defaultLightSource: 'uv' },
    // P2 - CO2 gantry laser
    'co2': { extId: 'P2', extName: 'P2', hasLightSourceMode: false },
    // P2S - CO2 gantry laser (newer)
    'p2s': { extId: 'P2S', extName: 'P2S', hasLightSourceMode: false },
    // P3 - CO2 gantry laser
    'p3': { extId: 'P3', extName: 'P3', hasLightSourceMode: false },
    // Legacy alias for 'f1' (diode was the old name)
    'diode': { extId: 'F1', extName: 'F1', hasLightSourceMode: true, lightSources: ['blue', 'red'], defaultLightSource: 'blue' },
};

export type LaserType = keyof typeof LASER_CONFIGS;

// Gantry laser types that support lower focus
const GANTRY_LASER_TYPES: LaserType[] = ['co2', 'p2s', 'p3'];

/**
 * Determine the laser type from a machine configuration
 * Used for setting correct extId/extName in XCS files
 */
export function getLaserTypeFromMachine(machine: {
    name: string;
    lasers?: { type: string; power: number }[];
}): LaserType {
    const name = machine.name.toLowerCase();

    // Check for specific xTool models by name (most specific first)

    // F2 Ultra variants
    if (name.includes('f2 ultra uv') || name.includes('f2ultrauv') || name.includes('f2 ultra-uv')) {
        return 'f2ultrauv';
    }
    if (name.includes('f2 ultra single') || name.includes('f2ultrasingle') || name.includes('f2 ultra (single)')) {
        return 'f2ultrasingle';
    }
    if (name.includes('f2 ultra') || name.includes('f2ultra')) {
        return 'f2ultra';
    }

    // F1 variants
    if (name.includes('f1 ultra') || name.includes('f1ultra')) {
        return 'f1ultra';
    }
    if (name.includes('f1')) {
        return 'f1';
    }

    // P-series gantry lasers
    // Check P3 first (before P2/P2S since 'p3' doesn't contain 'p2')
    if (name.includes('p3')) {
        return 'p3';
    }
    // Check P2S specifically (contains 'p2s' or 'p2 s')
    if (name.includes('p2s') || name.includes('p2 s')) {
        return 'p2s';
    }
    // Check P2 (original CO2 gantry)
    if (name.includes('p2')) {
        return 'co2';
    }

    // Check primary laser type from lasers array
    if (machine.lasers && machine.lasers.length > 0) {
        const primaryLaser = machine.lasers[0].type.toLowerCase();
        if (primaryLaser === 'co2') {
            return 'co2';
        }
        if (primaryLaser === 'fiber') {
            return 'f2ultra';  // Default fiber to F2 Ultra
        }
        if (primaryLaser === 'uv') {
            return 'f2ultrauv';
        }
    }

    // Default to F1 for all other machines
    return 'f1';
}

/**
 * Determine laser platform type: 'galvo' (F1, F2) or 'gantry' (P2, P2S, P3)
 * Gantry lasers support lower focus/cutting drop
 */
export function getLaserPlatform(laserType: LaserType): 'galvo' | 'gantry' {
    return GANTRY_LASER_TYPES.includes(laserType) ? 'gantry' : 'galvo';
}

/**
 * Check if a laser type supports light source mode selection (multiple lasers)
 */
export function hasLightSourceModeSupport(laserType: LaserType): boolean {
    const config = LASER_CONFIGS[laserType];
    return config?.hasLightSourceMode ?? false;
}

/**
 * Check if a laser has multiple light sources to choose from
 */
export function hasMultipleLightSources(laserType: LaserType): boolean {
    const config = LASER_CONFIGS[laserType];
    return (config?.lightSources?.length ?? 0) > 1;
}

// Light source labels and descriptions
const LIGHT_SOURCE_INFO: Record<LightSourceMode, { label: string; descriptions: Record<string, string> }> = {
    'blue': {
        label: 'Diode',
        descriptions: {
            'f1': '10W Blue Laser - Wood, Leather, Acrylic',
            'diode': '10W Blue Laser - Wood, Leather, Acrylic',
            'f1ultra': '20W Blue Laser - Wood, Leather, Acrylic',
            'f2ultra': '20W Blue Laser - Wood, Leather, Acrylic',
            'f2ultrasingle': '20W Blue Laser - Wood, Leather, Acrylic',
            'default': 'Blue Diode Laser - Wood, Leather, Acrylic'
        }
    },
    'red': {
        label: 'IR',
        descriptions: {
            'f1': '2W Infrared Laser - Metals, Plastics',
            'diode': '2W Infrared Laser - Metals, Plastics',
            'default': 'Infrared Laser - Metals, Plastics'
        }
    },
    'fiber': {
        label: 'Fiber',
        descriptions: {
            'f1ultra': '20W Fiber Laser - Metals, Jewelry',
            'f2ultra': '20W Fiber Laser - Metals, Jewelry',
            'default': 'Fiber Laser - Metals, Jewelry'
        }
    },
    'uv': {
        label: 'UV',
        descriptions: {
            'f2ultrauv': '5W UV Laser - Glass, Crystal, Plastics',
            'default': 'UV Laser - Glass, Crystal, Plastics'
        }
    }
};

/**
 * Get light source options for a laser type
 */
export function getLightSourceOptions(laserType: LaserType): { value: LightSourceMode; label: string; description: string }[] {
    const config = LASER_CONFIGS[laserType];
    if (!config?.lightSources) return [];

    return config.lightSources.map(mode => {
        const info = LIGHT_SOURCE_INFO[mode];
        return {
            value: mode,
            label: info.label,
            description: info.descriptions[laserType] || info.descriptions['default']
        };
    });
}

// ============================================================
// XCS INTERFACES
// ============================================================

interface XcsTransform {
    x: number;
    y: number;
}

interface XcsFill {
    paintType: 'color';
    visible: boolean;
    color: number;
    alpha: number;
}

interface XcsStroke {
    paintType: 'color';
    visible: boolean;
    color: number;
    alpha: number;
    width: number;
    cap: 'butt' | 'round' | 'square';
    join: 'miter' | 'round' | 'bevel';
    miterLimit: number;
    alignment: number;
}

interface XcsBaseShape {
    id: string;
    name: string | null;
    type: string;
    x: number;
    y: number;
    angle: number;
    scale: XcsTransform;
    skew: XcsTransform;
    pivot: XcsTransform;
    localSkew: XcsTransform;
    offsetX: number;
    offsetY: number;
    lockRatio: boolean;
    isClosePath: boolean;
    zOrder: number;
    groupTag: string;
    layerTag: string;
    layerColor: string;
    visible: boolean;
    originColor: string;
    enableTransform: boolean;
    visibleState: boolean;
    lockState: boolean;
    resourceOrigin: string;
    customData: Record<string, unknown>;
    rootComponentId: string;
    fill: XcsFill;
    stroke: XcsStroke;
    width: number;
    height: number;
    isFill: boolean;
    lineColor: number;
    fillColor: string;
}

interface XcsRectShape extends XcsBaseShape {
    type: 'RECT';
    radius: number;
    maxRadius: number;
}

interface XcsTextStyle {
    fontSize: number;
    fontFamily: string;
    fontSubfamily: string;
    fontSource: 'build-in' | 'user';
    letterSpacing: number;
    leading: number;
    align: 'left' | 'center' | 'right';
    isUppercase: boolean;
    isWeld: boolean;
}

interface XcsTextShape extends XcsBaseShape {
    type: 'TEXT';
    text: string;
    resolution: number;
    style: XcsTextStyle;
    fontData: {
        fontInfo: {
            unitsPerEm: number;
            lineHeight: number;
            ascent: number;
            descent: number;
            capHeight: number;
            xHeight: number;
            lineGap: number;
        };
        glyphData: Record<string, { dPath: string }>;
    };
    charJSONs: unknown[];
}

interface XcsPathShape extends XcsBaseShape {
    type: 'PATH';
    dPath: string;
    fillRule: 'nonzero' | 'evenodd';
    graphicX: number;
    graphicY: number;
    isCompoundPath: boolean;
}

// BITMAP shapes extend XcsBaseShape with image-specific properties
interface XcsBitmapShape extends XcsBaseShape {
    type: 'BITMAP';
    base64: string;            // Data URI: data:image/png;base64,...
    filterList: unknown[];     // Image filters applied
    grayValue: [number, number];  // Gray range [min, max]
    sharpness: number;
    brightness: number;
    contrast: number;
    saturation: number;
    temperature: number;
    tone: number;
    colorInverted: boolean;
    filterAttrsMap: Record<string, unknown>;
    mask: null | unknown;
    originWidth: number;       // Original image width in pixels
    originHeight: number;      // Original image height in pixels
    modifyData: Record<string, unknown>;
    currentUrl: string;
    dpi: { dpiX: number; dpiY: number };
    isGray: boolean;
    originAutoAdjust: null | unknown;
    autoStrength: number;
    filterList_V2: unknown[];
}

type XcsShape = XcsRectShape | XcsTextShape | XcsPathShape | XcsBitmapShape;

interface XcsLayer {
    name: string;
    order: number;
    visible: boolean;
}

interface XcsCanvasPanel {
    id: string;
    title: string;
    layerData: Record<string, XcsLayer>;
    groupData: Record<string, unknown>;
    displays: XcsShape[];
    extendInfo?: Record<string, unknown>;
}

// Parameter structure for VECTOR_CUTTING (cut through material)
interface XcsVectorCuttingParams {
    processingLightSource: LightSourceMode;
    power: number;              // 0-100 %
    speed: number;              // mm/s
    repeat: number;             // passes
    enableKerf: boolean;
    kerfDistance: number;       // mm
    enableBreakPoint: boolean;
    breakPointSize: number;
    breakPointCount: number;
    breakPointMode: 'count' | 'distance';
    breakPointDistance: number;
    breakPointPower: number;
    frequency: number;          // typically 40 for most lasers
}

// Parameter structure for VECTOR_ENGRAVING (score/outline)
interface XcsVectorEngravingParams {
    processingLightSource: LightSourceMode;
    power: number;
    speed: number;
    repeat: number;
    frequency: number;
    enableKerf: boolean;
    kerfDistance: number;
}

// Parameter structure for FILL_VECTOR_ENGRAVING (raster fill)
interface XcsFillEngravingParams {
    processingLightSource: LightSourceMode;
    power: number;
    speed: number;
    repeat: number;
    frequency: number;
    bitmapEngraveMode: 'normal' | 'reverse';
    density: number;            // lines/cm: 10-300
    dotDuration: number;
    dpi: number;
    bitmapScanMode: 'zMode' | 'oneWay';
    scanAngle: number;
    angleType: number;
    crossAngle: boolean;
}

// Parameter structure for INTAGLIO (3D relief carving)
interface XcsIntaglioParams {
    processingLightSource: LightSourceMode;
    power: number;
    speed: number;
    repeat: number;
    frequency: number;
    bitmapEngraveMode: 'normal' | 'reverse';
    density: number;
    bitmapScanMode: 'zMode' | 'oneWay';
    sliceNumber: number;
    processAngle: number;
    zAxisMove: boolean;
    zLayers: number;
    zDecline: number;
}

// Parameter structure for BITMAP_ENGRAVING (raster image engraving)
interface XcsBitmapEngravingParams {
    dotDuration: number;
    power: number;
    dpi: number;
    powerMinMaxRange: [number, number];
    speed: number;
    repeat: number;
    bitmapMode: 'grayscale' | 'binary' | 'halftone';
    bitmapScanMode: 'zMode' | 'oneWay';
    bitmapEngraveMode: 'dot' | 'line';
    processingLightSource: LightSourceMode;
}

// Full processing type config with material type and parameters
interface XcsProcessingTypeConfig<T> {
    materialType: 'customize' | 'official';
    planType: LightSourceMode | 'official';
    parameter: {
        customize: T;
    };
}

// Complete process data for all processing types
interface XcsProcessData {
    VECTOR_CUTTING?: XcsProcessingTypeConfig<XcsVectorCuttingParams>;
    VECTOR_ENGRAVING?: XcsProcessingTypeConfig<XcsVectorEngravingParams>;
    FILL_VECTOR_ENGRAVING?: XcsProcessingTypeConfig<XcsFillEngravingParams>;
    INTAGLIO?: XcsProcessingTypeConfig<XcsIntaglioParams>;
    BITMAP_ENGRAVING?: XcsProcessingTypeConfig<XcsBitmapEngravingParams>;
}

interface XcsShapeProcessingData {
    isFill: boolean;
    type: string;
    processingType: 'VECTOR_CUTTING' | 'VECTOR_ENGRAVING' | 'FILL_VECTOR_ENGRAVING' | 'BITMAP_ENGRAVING';
    data: XcsProcessData;
    processIgnore: boolean;
    sourceId?: string;
}

interface XcsDeviceData {
    id: string;  // extId
    power: number[] | null;
    data: {
        dataType: 'Map';
        value: [string, {  // canvasId
            mode: 'LASER_PLANE';
            data: {
                LASER_PLANE: {
                    material: number;
                    lightSourceMode?: LightSourceMode;
                    thickness?: null;
                    perimeter?: null;
                    diameter?: null;
                    isProcessByLayer?: boolean;
                    pathPlanning?: string;
                    fillPlanning?: string;
                    dreedyTsp?: boolean;
                    avoidSmokeModal?: boolean;
                    scanDirection?: string;
                    xcsUsed?: unknown[];
                };
            };
            displays: {
                dataType: 'Map';
                value: [string, XcsShapeProcessingData][];
            };
        }][];
    };
    materialList: unknown[];
    materialTypeList: unknown[];
    customProjectData: Record<string, unknown>;
}

interface XcsFile {
    canvasId: string;
    canvas: XcsCanvasPanel[];
    extId: string;
    extName: string;
    device: XcsDeviceData;
    version: string;
    created: number;
    modify: number;
    ua: string;
    meta: { version: string; date: number; ua: string }[];
    cover: string;
    minRequiredVersion: string;
    appMinRequiredVersion: string;
    webMinRequiredVersion: string;
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Generate a UUID v4
 */
function generateUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Create base shape properties shared by all XCS shapes
 */
function createBaseShape(
    id: string,
    type: string,
    x: number,
    y: number,
    width: number,
    height: number,
    layerTag: string,
    isFill: boolean,
    zOrder: number
): XcsBaseShape {
    return {
        id,
        name: null,
        type,
        x,
        y,
        angle: 0,
        scale: { x: 1, y: 1 },
        skew: { x: 0, y: 0 },
        pivot: { x: 0, y: 0 },
        localSkew: { x: 0, y: 0 },
        offsetX: x + width / 2,
        offsetY: y + height / 2,
        lockRatio: true,
        isClosePath: true,
        zOrder,
        groupTag: generateUuid(),
        layerTag,
        layerColor: layerTag,
        visible: true,
        originColor: '#000000',
        enableTransform: true,
        visibleState: true,
        lockState: false,
        resourceOrigin: '',
        customData: {},
        rootComponentId: '',
        fill: {
            paintType: 'color',
            visible: isFill,
            color: 0,
            alpha: 1
        },
        stroke: {
            paintType: 'color',
            visible: !isFill,
            color: 0,
            alpha: 1,
            width: 1,
            cap: 'butt',
            join: 'miter',
            miterLimit: 4,
            alignment: 0.5
        },
        width,
        height,
        isFill,
        lineColor: 16421416,
        fillColor: '#f9932b'
    };
}

/**
 * Create an XCS RECT shape
 */
function createXcsRect(
    x: number,
    y: number,
    width: number,
    height: number,
    layerTag: string,
    zOrder: number
): XcsRectShape {
    const id = generateUuid();
    const base = createBaseShape(id, 'RECT', x, y, width, height, layerTag, false, zOrder);

    return {
        ...base,
        type: 'RECT',
        radius: 0,
        maxRadius: Math.min(width, height) / 2
    } as XcsRectShape;
}

/**
 * Create an XCS BITMAP shape for raster images
 * BITMAP extends XcsBaseShape with image-specific properties
 */
function createXcsBitmap(
    base64: string,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    zOrder: number,
    layerTag: string = XCS_LAYER_BLACK,
    originalWidth?: number,
    originalHeight?: number
): XcsBitmapShape {
    const id = generateUuid();

    // Calculate scale from original dimensions if provided
    const origW = originalWidth || width;
    const origH = originalHeight || height;
    const scaleX = width / origW;
    const scaleY = height / origH;

    // Calculate DPI based on scale (assume base DPI of ~500)
    const baseDpi = 500;
    const dpiX = baseDpi / scaleX;
    const dpiY = baseDpi / scaleY;

    // Create base shape properties
    const base = createBaseShape(id, 'BITMAP', x, y, origW, origH, layerTag, true, zOrder);

    // Override scale since we calculated it from original dimensions
    base.scale = { x: scaleX, y: scaleY };
    base.angle = rotation;

    return {
        ...base,
        type: 'BITMAP',
        base64,
        filterList: [],
        grayValue: [0, 255],
        sharpness: 50,
        brightness: 0,
        contrast: 0,
        saturation: 0,
        temperature: 0,
        tone: 0,
        colorInverted: false,
        filterAttrsMap: {
            emboss: { strength: 5 },
            halftone: { radius: 4, angle: 45 },
            binary: { threshold: 128 },
            sketch: { strength: 2 },
            dot: { angle: 45, scale: 14 }
        },
        mask: null,
        originWidth: Math.round(origW),
        originHeight: Math.round(origH),
        modifyData: {},
        currentUrl: '',
        dpi: { dpiX, dpiY },
        isGray: false,
        originAutoAdjust: null,
        autoStrength: 0,
        filterList_V2: []
    } as XcsBitmapShape;
}

/**
 * Split font name into family and subfamily
 * "Cinzel-SemiBold" -> { family: "Cinzel", subfamily: "SemiBold" }
 * "Arial" -> { family: "Arial", subfamily: "Regular" }
 */
function splitFontName(fontName: string): { family: string; subfamily: string } {
    const parsed = parseFontFamily(fontName);
    return {
        family: parsed.family,
        subfamily: parsed.weight || parsed.style || 'Regular'
    };
}

// Note: Using fontSource='build-in' lets xTool use system fonts directly
// Users need to have the same fonts installed in both kiosk app and xTool

/**
 * Create an XCS TEXT shape
 */
function createXcsText(
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    fontSize: number,
    fontFamily: string,
    layerTag: string,
    isFill: boolean,
    zOrder: number,
    fontData?: {
        fontInfo: {
            unitsPerEm: number;
            lineHeight: number;
            ascent: number;
            descent: number;
            capHeight: number;
            xHeight: number;
            lineGap: number;
        };
        glyphData: Record<string, {
            dPath: string;
            advanceWidth: number;
            advanceHeight: number;
            leftBearing: number;
            topBearing: number;
            bbox: { minX: number; minY: number; maxX: number; maxY: number };
        }>;
    },
    // Optional: preserved positioning for lossless round-trip export
    originalPosition?: {
        x: number;
        y: number;
        width: number;
        height: number;
        scale: { x: number; y: number };
    }
): XcsTextShape {
    const id = generateUuid();
    const { family, subfamily } = splitFontName(fontFamily);

    // Use 'build-in' to avoid orange "?" icon in xTool UI
    // But still provide glyph data for actual rendering
    const fontSource = 'build-in';

    // Use provided fontData or fallback to minimal metrics
    const finalFontInfo = fontData?.fontInfo || {
        unitsPerEm: 1000,
        lineHeight: fontSize * 1.2,
        ascent: fontSize * 0.8,
        descent: fontSize * -0.2,
        capHeight: fontSize * 0.7,
        xHeight: fontSize * 0.5,
        lineGap: 0
    };

    const finalGlyphData = fontData?.glyphData || {};

    // If we have preserved original positioning, use it for exact round-trip
    // Otherwise calculate new positioning
    let finalX: number;
    let finalY: number;
    let finalWidth: number;
    let finalHeight: number;
    let textScale: number;

    if (originalPosition) {
        // Use preserved values exactly - no recalculation
        finalX = originalPosition.x;
        finalY = originalPosition.y;
        finalWidth = originalPosition.width;
        finalHeight = originalPosition.height;
        textScale = originalPosition.scale.x;
    } else {
        // Calculate new positioning (for newly created text)
        // xTool TEXT rendering: glyphs are stored at reference size 72 in fontData.
        // The scale property must scale these down to match fontSize.
        //
        // How xTool renders: glyph_coords * scale = rendered_coords
        // Since glyphs are at size 72, we need scale = fontSize / 72
        textScale = fontSize / 72;

        // xTool text positioning with custom glyph data:
        // When providing fontData.glyphData, xTool ignores align:'center' and renders
        // glyphs starting from x position going right. We must manually center.
        //
        // Calculate actual text width from glyph advanceWidths (at size 72, then scale)
        let textWidthAt72 = 0;
        for (const char of text) {
            const glyph = finalGlyphData[char];
            if (glyph?.advanceWidth) {
                textWidthAt72 += glyph.advanceWidth;
            }
        }
        const actualTextWidth = textWidthAt72 * textScale;

        // Center the text: x = box_left + (box_width - text_width) / 2
        finalX = x + (width - actualTextWidth) / 2;

        // Center text vertically using actual glyph bounding box height.
        // Compute text height from per-glyph bboxes (at reference size 72).
        let minGlyphY = Infinity;
        let maxGlyphY = -Infinity;
        for (const char of text) {
            const glyph = finalGlyphData[char];
            if (glyph?.bbox) {
                minGlyphY = Math.min(minGlyphY, glyph.bbox.minY);
                maxGlyphY = Math.max(maxGlyphY, glyph.bbox.maxY);
            }
        }
        const textHeightAt72 = (minGlyphY !== Infinity) ? (maxGlyphY - minGlyphY) : 0;
        const renderedTextHeight = textHeightAt72 * textScale;
        // When height-constrained: renderedTextHeight ≈ height*0.996, offset ≈ height*0.002 (matches original bias)
        // When width-constrained: renderedTextHeight < height, offset centers text properly
        finalY = (renderedTextHeight > 0) ? y + (height - renderedTextHeight) / 2 : y + (height * 0.002);
        finalWidth = width;
        finalHeight = height;
    }

    const base = createBaseShape(id, 'TEXT', finalX, finalY, finalWidth, finalHeight, layerTag, isFill, zOrder);

    return {
        ...base,
        type: 'TEXT',
        x: finalX,  // Use preserved or calculated centered position
        y: finalY,
        width: finalWidth,
        height: finalHeight,
        scale: { x: textScale, y: textScale },
        offsetX: x + width / 2,  // Anchor at box center (for selection/transform)
        offsetY: y + height / 2,  // Vertical center
        text,
        resolution: 1,
        style: {
            fontSize,
            fontFamily: family,
            fontSubfamily: subfamily,
            fontSource,
            letterSpacing: 0,
            leading: 0,
            align: 'center',
            isUppercase: false,
            isWeld: true  // Weld overlapping glyph outlines
        },
        fontData: {
            fontInfo: finalFontInfo,
            glyphData: finalGlyphData
        },
        charJSONs: []
    } as XcsTextShape;
}

/**
 * Create an XCS PATH shape
 */
export function createXcsPath(
    dPath: string,
    x: number,
    y: number,
    width: number,
    height: number,
    layerTag: string,
    isFill: boolean,
    zOrder: number,
    isCompoundPath: boolean = false,
    fillRule: 'nonzero' | 'evenodd' = 'nonzero'
): XcsPathShape {
    const id = generateUuid();
    const base = createBaseShape(id, 'PATH', x, y, width, height, layerTag, isFill, zOrder);

    // For PATH shapes, offsetX/offsetY should be 0 (not center-based like text)
    return {
        ...base,
        type: 'PATH',
        offsetX: 0,
        offsetY: 0,
        isClosePath: dPath.toLowerCase().includes('z'),
        dPath,
        fillRule,
        graphicX: 0,
        graphicY: 0,
        isCompoundPath
    } as XcsPathShape;
}

// ============================================================
// GLYPH EXTRACTION (uses loadFont, pathToSvg from textGenerator)
// ============================================================

/**
 * Extract glyph data for specific characters from a font
 *
 * IMPORTANT: Glyphs are extracted at a FIXED size of 72 units.
 * xTool applies scale = fontSize/72 to get the final rendered size.
 * Example: glyph at 72 units * scale 0.111 (8/72) = 8mm final size
 */
function extractGlyphData(
    font: opentype.Font,
    characters: string,
    _fontSize: number  // Not used for glyph extraction, kept for API compatibility
): {
    fontInfo: {
        unitsPerEm: number;
        lineHeight: number;
        ascent: number;
        descent: number;
        capHeight: number;
        xHeight: number;
        lineGap: number;
    };
    glyphData: Record<string, {
        dPath: string;
        advanceWidth: number;
        advanceHeight: number;
        leftBearing: number;
        topBearing: number;
        bbox: { minX: number; minY: number; maxX: number; maxY: number };
    }>;
} {
    const unitsPerEm = font.unitsPerEm;

    // Extract glyphs at fixed size 72 (xTool standard)
    // xTool's scale = fontSize/72 will handle final sizing
    const GLYPH_SIZE = 72;
    const scale = GLYPH_SIZE / unitsPerEm;

    // Extract font metrics at glyph size
    const ascent = font.ascender * scale;
    const descent = font.descender * scale;
    const lineGap = (font.tables?.os2?.sTypoLineGap || 0) * scale;
    const lineHeight = ascent - descent + lineGap;

    // Get cap height and x-height (with fallbacks)
    const capHeight = (font.tables?.os2?.sCapHeight || font.ascender * 0.7) * scale;
    const xHeight = (font.tables?.os2?.sxHeight || font.ascender * 0.5) * scale;

    const fontInfo = {
        unitsPerEm,
        lineHeight,
        ascent,
        descent,
        capHeight,
        xHeight,
        lineGap
    };

    // Extract unique characters
    const uniqueChars = [...new Set(characters.split(''))];
    const glyphData: Record<string, {
        dPath: string;
        advanceWidth: number;
        advanceHeight: number;
        leftBearing: number;
        topBearing: number;
        bbox: { minX: number; minY: number; maxX: number; maxY: number };
    }> = {};

    for (const char of uniqueChars) {
        const glyph = font.charToGlyph(char);
        if (!glyph) continue;

        // Get the path at FIXED size 72 (not fontSize!)
        // xTool's scale factor will handle final sizing
        const path = glyph.getPath(0, 0, GLYPH_SIZE);
        const dPath = pathToSvg(path);

        // Get metrics at glyph size
        const advanceWidth = (glyph.advanceWidth || 0) * scale;
        const advanceHeight = lineHeight;
        const leftBearing = (glyph.leftSideBearing || 0) * scale;

        // Get bounding box
        const bbox = glyph.getBoundingBox();
        const scaledBbox = {
            minX: bbox.x1 * scale,
            minY: bbox.y1 * scale,
            maxX: bbox.x2 * scale,
            maxY: bbox.y2 * scale
        };

        // Top bearing (distance from top to glyph top)
        const topBearing = ascent - scaledBbox.maxY;

        glyphData[char] = {
            dPath,
            advanceWidth,
            advanceHeight,
            leftBearing,
            topBearing,
            bbox: scaledBbox
        };
    }

    return { fontInfo, glyphData };
}

/**
 * Extract font data for text rendering
 * Falls back to Roboto if font not found in database
 */
async function extractFontData(
    text: string,
    fontFamily: string,
    fontSize: number,
    fonts: Font[]
): Promise<{
    fontInfo: {
        unitsPerEm: number;
        lineHeight: number;
        ascent: number;
        descent: number;
        capHeight: number;
        xHeight: number;
        lineGap: number;
    };
    glyphData: Record<string, {
        dPath: string;
        advanceWidth: number;
        advanceHeight: number;
        leftBearing: number;
        topBearing: number;
        bbox: { minX: number; minY: number; maxX: number; maxY: number };
    }>;
}> {
    try {
        // Convert to textGenerator Font type (filter out fonts without URLs)
        const textGenFonts: TextGenFont[] = fonts
            .filter(f => f.url)
            .map(f => ({ id: f.id, name: f.name, family: f.family, url: f.url! }));

        // Try to find the font in the database
        const dbFont = findFontByName(textGenFonts, fontFamily);

        let font: opentype.Font;

        if (dbFont && dbFont.url) {
            font = await loadFont(dbFont.url);
        } else {
            // Fall back to bundled Roboto
            font = await loadFont(ROBOTO_REGULAR_BASE64);
        }

        return extractGlyphData(font, text, fontSize);
    } catch (error) {
        console.error('[extractFontData] Error extracting font data:', error);
        // Return minimal fallback
        return {
            fontInfo: {
                unitsPerEm: 1000,
                lineHeight: fontSize * 1.2,
                ascent: fontSize * 0.8,
                descent: fontSize * -0.2,
                capHeight: fontSize * 0.7,
                xHeight: fontSize * 0.5,
                lineGap: 0
            },
            glyphData: {}
        };
    }
}

// ============================================================
// SVG PATH PARSING AND TRANSFORMATION
// ============================================================
// PathCommand interface and parsePathCommands moved to pathUtils.ts - imported above

/**
 * Transform path commands from SVG viewBox coordinates to absolute canvas coordinates
 */
function transformPathCommands(
    commands: PathCommand[],
    scaleX: number,
    scaleY: number,
    offsetX: number,
    offsetY: number,
    viewBoxX: number,
    viewBoxY: number
): string {
    let currentX = 0;
    let currentY = 0;
    let startX = 0;
    let startY = 0;
    const result: string[] = [];

    for (const cmd of commands) {
        const { type, values } = cmd;
        const isRelative = type === type.toLowerCase();
        const cmdType = type.toUpperCase();

        switch (cmdType) {
            case 'M': // moveto
                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i];
                    let y = values[i + 1];

                    if (isRelative && i > 0) {
                        x = currentX + x;
                        y = currentY + y;
                    } else if (isRelative) {
                        x = currentX + x;
                        y = currentY + y;
                    } else {
                        // Absolute - adjust for viewBox origin
                        x = x - viewBoxX;
                        y = y - viewBoxY;
                    }

                    // Transform to canvas coordinates
                    const canvasX = x * scaleX + offsetX;
                    const canvasY = y * scaleY + offsetY;

                    result.push(i === 0 ? `M${canvasX},${canvasY}` : `L${canvasX},${canvasY}`);
                    currentX = x;
                    currentY = y;
                    if (i === 0) {
                        startX = x;
                        startY = y;
                    }
                }
                break;

            case 'L': // lineto
                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i];
                    let y = values[i + 1];

                    if (isRelative) {
                        x = currentX + x;
                        y = currentY + y;
                    } else {
                        x = x - viewBoxX;
                        y = y - viewBoxY;
                    }

                    const canvasX = x * scaleX + offsetX;
                    const canvasY = y * scaleY + offsetY;

                    result.push(`L${canvasX},${canvasY}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'H': // horizontal line
                for (const val of values) {
                    let x = isRelative ? currentX + val : val - viewBoxX;
                    const canvasX = x * scaleX + offsetX;
                    const canvasY = currentY * scaleY + offsetY;

                    result.push(`L${canvasX},${canvasY}`);
                    currentX = x;
                }
                break;

            case 'V': // vertical line
                for (const val of values) {
                    let y = isRelative ? currentY + val : val - viewBoxY;
                    const canvasX = currentX * scaleX + offsetX;
                    const canvasY = y * scaleY + offsetY;

                    result.push(`L${canvasX},${canvasY}`);
                    currentY = y;
                }
                break;

            case 'C': // cubic bezier
                for (let i = 0; i < values.length; i += 6) {
                    let x1 = values[i], y1 = values[i + 1];
                    let x2 = values[i + 2], y2 = values[i + 3];
                    let x = values[i + 4], y = values[i + 5];

                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    } else {
                        x1 -= viewBoxX; y1 -= viewBoxY;
                        x2 -= viewBoxX; y2 -= viewBoxY;
                        x -= viewBoxX; y -= viewBoxY;
                    }

                    const cx1 = x1 * scaleX + offsetX, cy1 = y1 * scaleY + offsetY;
                    const cx2 = x2 * scaleX + offsetX, cy2 = y2 * scaleY + offsetY;
                    const cx = x * scaleX + offsetX, cy = y * scaleY + offsetY;

                    result.push(`C${cx1},${cy1},${cx2},${cy2},${cx},${cy}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'S': // smooth cubic
                for (let i = 0; i < values.length; i += 4) {
                    let x2 = values[i], y2 = values[i + 1];
                    let x = values[i + 2], y = values[i + 3];

                    if (isRelative) {
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    } else {
                        x2 -= viewBoxX; y2 -= viewBoxY;
                        x -= viewBoxX; y -= viewBoxY;
                    }

                    const cx2 = x2 * scaleX + offsetX, cy2 = y2 * scaleY + offsetY;
                    const cx = x * scaleX + offsetX, cy = y * scaleY + offsetY;

                    result.push(`S${cx2},${cy2},${cx},${cy}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'Q': // quadratic bezier
                for (let i = 0; i < values.length; i += 4) {
                    let x1 = values[i], y1 = values[i + 1];
                    let x = values[i + 2], y = values[i + 3];

                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x += currentX; y += currentY;
                    } else {
                        x1 -= viewBoxX; y1 -= viewBoxY;
                        x -= viewBoxX; y -= viewBoxY;
                    }

                    const cx1 = x1 * scaleX + offsetX, cy1 = y1 * scaleY + offsetY;
                    const cx = x * scaleX + offsetX, cy = y * scaleY + offsetY;

                    result.push(`Q${cx1},${cy1},${cx},${cy}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'T': // smooth quadratic
                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i], y = values[i + 1];

                    if (isRelative) {
                        x += currentX; y += currentY;
                    } else {
                        x -= viewBoxX; y -= viewBoxY;
                    }

                    const cx = x * scaleX + offsetX, cy = y * scaleY + offsetY;

                    result.push(`T${cx},${cy}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'A': // arc
                for (let i = 0; i < values.length; i += 7) {
                    const rx = values[i] * scaleX;
                    const ry = values[i + 1] * scaleY;
                    const rotation = values[i + 2];
                    const largeArc = values[i + 3];
                    const sweep = values[i + 4];
                    let x = values[i + 5], y = values[i + 6];

                    if (isRelative) {
                        x += currentX; y += currentY;
                    } else {
                        x -= viewBoxX; y -= viewBoxY;
                    }

                    const cx = x * scaleX + offsetX, cy = y * scaleY + offsetY;

                    result.push(`A${rx},${ry},${rotation},${largeArc},${sweep},${cx},${cy}`);
                    currentX = x;
                    currentY = y;
                }
                break;

            case 'Z': // closepath
                result.push('Z');
                currentX = startX;
                currentY = startY;
                break;
        }
    }

    return result.join('');
}

/**
 * Calculate bounding box from path commands
 */
function calculatePathBounds(commands: PathCommand[]): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let currentX = 0, currentY = 0;

    for (const cmd of commands) {
        const { type, values } = cmd;
        const isRelative = type === type.toLowerCase();
        const cmdType = type.toUpperCase();

        // Extract coordinates and update bounds
        switch (cmdType) {
            case 'M':
            case 'L':
            case 'T':
                for (let i = 0; i < values.length; i += 2) {
                    let x = values[i], y = values[i + 1];
                    if (isRelative) { x += currentX; y += currentY; }
                    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
                    currentX = x; currentY = y;
                }
                break;
            case 'H':
                for (const x of values) {
                    const absX = isRelative ? currentX + x : x;
                    minX = Math.min(minX, absX); maxX = Math.max(maxX, absX);
                    currentX = absX;
                }
                break;
            case 'V':
                for (const y of values) {
                    const absY = isRelative ? currentY + y : y;
                    minY = Math.min(minY, absY); maxY = Math.max(maxY, absY);
                    currentY = absY;
                }
                break;
            case 'C':
                for (let i = 0; i < values.length; i += 6) {
                    let x1 = values[i], y1 = values[i + 1];
                    let x2 = values[i + 2], y2 = values[i + 3];
                    let x = values[i + 4], y = values[i + 5];
                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    }
                    // Include control points in bounds (approximation)
                    minX = Math.min(minX, x1, x2, x); maxX = Math.max(maxX, x1, x2, x);
                    minY = Math.min(minY, y1, y2, y); maxY = Math.max(maxY, y1, y2, y);
                    currentX = x; currentY = y;
                }
                break;
            case 'S':
                for (let i = 0; i < values.length; i += 4) {
                    let x2 = values[i], y2 = values[i + 1];
                    let x = values[i + 2], y = values[i + 3];
                    if (isRelative) {
                        x2 += currentX; y2 += currentY;
                        x += currentX; y += currentY;
                    }
                    minX = Math.min(minX, x2, x); maxX = Math.max(maxX, x2, x);
                    minY = Math.min(minY, y2, y); maxY = Math.max(maxY, y2, y);
                    currentX = x; currentY = y;
                }
                break;
            case 'Q':
                for (let i = 0; i < values.length; i += 4) {
                    let x1 = values[i], y1 = values[i + 1];
                    let x = values[i + 2], y = values[i + 3];
                    if (isRelative) {
                        x1 += currentX; y1 += currentY;
                        x += currentX; y += currentY;
                    }
                    minX = Math.min(minX, x1, x); maxX = Math.max(maxX, x1, x);
                    minY = Math.min(minY, y1, y); maxY = Math.max(maxY, y1, y);
                    currentX = x; currentY = y;
                }
                break;
            case 'A':
                for (let i = 0; i < values.length; i += 7) {
                    let x = values[i + 5], y = values[i + 6];
                    if (isRelative) { x += currentX; y += currentY; }
                    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
                    currentX = x; currentY = y;
                }
                break;
        }
    }

    return { minX, minY, maxX, maxY };
}

/**
 * Convert a circle to an SVG path using bezier curves
 * Uses the standard bezier approximation with k = 0.552284749831
 */
function circleToPath(cx: number, cy: number, r: number): string {
    const k = r * 0.552284749831; // Magic number for bezier circle approximation
    return `M${cx},${cy - r} ` +
           `C${cx + k},${cy - r} ${cx + r},${cy - k} ${cx + r},${cy} ` +
           `C${cx + r},${cy + k} ${cx + k},${cy + r} ${cx},${cy + r} ` +
           `C${cx - k},${cy + r} ${cx - r},${cy + k} ${cx - r},${cy} ` +
           `C${cx - r},${cy - k} ${cx - k},${cy - r} ${cx},${cy - r} Z`;
}

/**
 * Convert an ellipse to an SVG path using bezier curves
 * Uses the standard bezier approximation with k = 0.552284749831
 */
function ellipseToPath(cx: number, cy: number, rx: number, ry: number): string {
    const kx = rx * 0.552284749831;
    const ky = ry * 0.552284749831;
    return `M${cx},${cy - ry} ` +
           `C${cx + kx},${cy - ry} ${cx + rx},${cy - ky} ${cx + rx},${cy} ` +
           `C${cx + rx},${cy + ky} ${cx + kx},${cy + ry} ${cx},${cy + ry} ` +
           `C${cx - kx},${cy + ry} ${cx - rx},${cy + ky} ${cx - rx},${cy} ` +
           `C${cx - rx},${cy - ky} ${cx - kx},${cy - ry} ${cx},${cy - ry} Z`;
}

/**
 * Extract path elements from SVG and convert to XCS format
 */
function extractSvgPaths(
    svgData: string,
    shapeX: number,
    shapeY: number,
    shapeWidth: number,
    shapeHeight: number
): { paths: { dPath: string; fillRule: 'nonzero' | 'evenodd' }[]; bounds: { x: number; y: number; width: number; height: number } } | null {
    try {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
        const svgElem = svgDoc.querySelector('svg');

        if (!svgElem) {
            console.warn('[extractSvgPaths] No SVG element found');
            return null;
        }

        // Get viewBox dimensions
        let viewBoxX = 0, viewBoxY = 0, viewBoxWidth = shapeWidth, viewBoxHeight = shapeHeight;

        const viewBox = svgElem.getAttribute('viewBox');
        if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number);
            if (parts.length === 4) {
                viewBoxX = parts[0];
                viewBoxY = parts[1];
                viewBoxWidth = parts[2];
                viewBoxHeight = parts[3];
            }
        } else {
            // Fall back to width/height attributes
            const widthAttr = svgElem.getAttribute('width');
            const heightAttr = svgElem.getAttribute('height');
            if (widthAttr) viewBoxWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || viewBoxWidth;
            if (heightAttr) viewBoxHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || viewBoxHeight;
        }

        // Calculate scale to fit graphic into shape bounds (maintain aspect ratio)
        const scaleX = shapeWidth / viewBoxWidth;
        const scaleY = shapeHeight / viewBoxHeight;
        const scale = Math.min(scaleX, scaleY);

        // Center the graphic if aspect ratios don't match
        const scaledWidth = viewBoxWidth * scale;
        const scaledHeight = viewBoxHeight * scale;
        const offsetX = shapeX + (shapeWidth - scaledWidth) / 2;
        const offsetY = shapeY + (shapeHeight - scaledHeight) / 2;

        // Extract all path elements
        const pathElements = svgElem.querySelectorAll('path');
        const paths: { dPath: string; fillRule: 'nonzero' | 'evenodd' }[] = [];
        let globalMinX = Infinity, globalMinY = Infinity, globalMaxX = -Infinity, globalMaxY = -Infinity;

        // Check if there's a <g> with transform wrapping paths (used by STAR/POLYGON SVGs)
        // If so, we need to apply that transform to get correct bounds
        const gTransformElem = svgElem.querySelector('g[transform]');
        let gScaleX = 1, gScaleY = 1, gTranslateX = 0, gTranslateY = 0;
        if (gTransformElem) {
            const transformAttr = gTransformElem.getAttribute('transform') || '';
            // Parse scale(sx, sy) and translate(tx, ty)
            const scaleMatch = transformAttr.match(/scale\(\s*([^,\s]+)(?:\s*,\s*([^)\s]+))?\s*\)/);
            if (scaleMatch) {
                gScaleX = parseFloat(scaleMatch[1]) || 1;
                gScaleY = parseFloat(scaleMatch[2] || scaleMatch[1]) || gScaleX;
            }
            const translateMatch = transformAttr.match(/translate\(\s*([^,\s]+)(?:\s*,\s*([^)\s]+))?\s*\)/);
            if (translateMatch) {
                gTranslateX = parseFloat(translateMatch[1]) || 0;
                gTranslateY = parseFloat(translateMatch[2] || '0') || 0;
            }
        }

        pathElements.forEach(pathElem => {
            const d = pathElem.getAttribute('d');
            if (!d) return;

            const commands = parsePathCommands(d);
            if (commands.length === 0) return;

            // Apply <g transform> to commands first if present
            // SVG transform="scale(sx,sy) translate(tx,ty)" means: first translate, then scale
            // So point (x,y) becomes ((x + tx) * sx, (y + ty) * sy)
            let commandsToTransform = commands;
            if (gScaleX !== 1 || gScaleY !== 1 || gTranslateX !== 0 || gTranslateY !== 0) {
                commandsToTransform = commands.map(cmd => {
                    const newValues = [...cmd.values];
                    const cmdType = cmd.type.toUpperCase();

                    // For absolute commands, transform all coordinate pairs
                    // For relative commands, only scale (translate doesn't apply to deltas)
                    const isRelative = cmd.type === cmd.type.toLowerCase();

                    switch (cmdType) {
                        case 'M': case 'L': case 'T':
                            // x,y pairs
                            for (let i = 0; i < newValues.length; i += 2) {
                                if (isRelative) {
                                    newValues[i] *= gScaleX;
                                    newValues[i + 1] *= gScaleY;
                                } else {
                                    newValues[i] = (newValues[i] + gTranslateX) * gScaleX;
                                    newValues[i + 1] = (newValues[i + 1] + gTranslateY) * gScaleY;
                                }
                            }
                            break;
                        case 'H':
                            // x only
                            for (let i = 0; i < newValues.length; i++) {
                                if (isRelative) {
                                    newValues[i] *= gScaleX;
                                } else {
                                    newValues[i] = (newValues[i] + gTranslateX) * gScaleX;
                                }
                            }
                            break;
                        case 'V':
                            // y only
                            for (let i = 0; i < newValues.length; i++) {
                                if (isRelative) {
                                    newValues[i] *= gScaleY;
                                } else {
                                    newValues[i] = (newValues[i] + gTranslateY) * gScaleY;
                                }
                            }
                            break;
                        case 'C':
                            // x1,y1,x2,y2,x,y
                            for (let i = 0; i < newValues.length; i += 6) {
                                for (let j = 0; j < 6; j += 2) {
                                    if (isRelative) {
                                        newValues[i + j] *= gScaleX;
                                        newValues[i + j + 1] *= gScaleY;
                                    } else {
                                        newValues[i + j] = (newValues[i + j] + gTranslateX) * gScaleX;
                                        newValues[i + j + 1] = (newValues[i + j + 1] + gTranslateY) * gScaleY;
                                    }
                                }
                            }
                            break;
                        case 'S': case 'Q':
                            // x1,y1,x,y or x2,y2,x,y
                            for (let i = 0; i < newValues.length; i += 4) {
                                for (let j = 0; j < 4; j += 2) {
                                    if (isRelative) {
                                        newValues[i + j] *= gScaleX;
                                        newValues[i + j + 1] *= gScaleY;
                                    } else {
                                        newValues[i + j] = (newValues[i + j] + gTranslateX) * gScaleX;
                                        newValues[i + j + 1] = (newValues[i + j + 1] + gTranslateY) * gScaleY;
                                    }
                                }
                            }
                            break;
                        case 'A':
                            // rx,ry,rotation,large,sweep,x,y
                            for (let i = 0; i < newValues.length; i += 7) {
                                // Scale radii
                                newValues[i] *= gScaleX;
                                newValues[i + 1] *= gScaleY;
                                // Transform endpoint
                                if (isRelative) {
                                    newValues[i + 5] *= gScaleX;
                                    newValues[i + 6] *= gScaleY;
                                } else {
                                    newValues[i + 5] = (newValues[i + 5] + gTranslateX) * gScaleX;
                                    newValues[i + 6] = (newValues[i + 6] + gTranslateY) * gScaleY;
                                }
                            }
                            break;
                        // Z has no coordinates
                    }
                    return { type: cmd.type, values: newValues };
                });
            }

            // Transform path to canvas coordinates
            const transformedPath = transformPathCommands(
                commandsToTransform,
                scale,
                scale,
                offsetX,
                offsetY,
                viewBoxX,
                viewBoxY
            );

            // Get fill rule
            const fillRuleAttr = pathElem.getAttribute('fill-rule') || 'nonzero';
            const fillRule = fillRuleAttr === 'evenodd' ? 'evenodd' : 'nonzero';

            paths.push({ dPath: transformedPath, fillRule });

            // Calculate bounds in canvas coordinates
            // Use commandsToTransform which already has <g transform> applied
            const pathBounds = calculatePathBounds(commandsToTransform);
            if (pathBounds.minX !== Infinity) {
                // Transform from viewBox space to canvas space
                const canvasMinX = (pathBounds.minX - viewBoxX) * scale + offsetX;
                const canvasMinY = (pathBounds.minY - viewBoxY) * scale + offsetY;
                const canvasMaxX = (pathBounds.maxX - viewBoxX) * scale + offsetX;
                const canvasMaxY = (pathBounds.maxY - viewBoxY) * scale + offsetY;

                globalMinX = Math.min(globalMinX, canvasMinX);
                globalMinY = Math.min(globalMinY, canvasMinY);
                globalMaxX = Math.max(globalMaxX, canvasMaxX);
                globalMaxY = Math.max(globalMaxY, canvasMaxY);
            }
        });

        // Extract all circle elements and convert to bezier paths
        const circleElements = svgElem.querySelectorAll('circle');
        circleElements.forEach(circleElem => {
            const cx = parseFloat(circleElem.getAttribute('cx') || '0');
            const cy = parseFloat(circleElem.getAttribute('cy') || '0');
            const r = parseFloat(circleElem.getAttribute('r') || '0');

            if (r > 0) {
                // Transform circle center and radius to canvas coordinates
                const canvasCx = (cx - viewBoxX) * scale + offsetX;
                const canvasCy = (cy - viewBoxY) * scale + offsetY;
                const canvasR = r * scale;

                // Convert to bezier path
                const circlePath = circleToPath(canvasCx, canvasCy, canvasR);
                paths.push({ dPath: circlePath, fillRule: 'nonzero' });

                // Update bounds
                globalMinX = Math.min(globalMinX, canvasCx - canvasR);
                globalMinY = Math.min(globalMinY, canvasCy - canvasR);
                globalMaxX = Math.max(globalMaxX, canvasCx + canvasR);
                globalMaxY = Math.max(globalMaxY, canvasCy + canvasR);
            }
        });

        // Extract all ellipse elements and convert to bezier paths
        const ellipseElements = svgElem.querySelectorAll('ellipse');
        ellipseElements.forEach(ellipseElem => {
            const cx = parseFloat(ellipseElem.getAttribute('cx') || '0');
            const cy = parseFloat(ellipseElem.getAttribute('cy') || '0');
            const rx = parseFloat(ellipseElem.getAttribute('rx') || '0');
            const ry = parseFloat(ellipseElem.getAttribute('ry') || '0');

            if (rx > 0 && ry > 0) {
                // Transform ellipse center and radii to canvas coordinates
                const canvasCx = (cx - viewBoxX) * scale + offsetX;
                const canvasCy = (cy - viewBoxY) * scale + offsetY;
                const canvasRx = rx * scale;
                const canvasRy = ry * scale;

                // Convert to bezier path
                const ellipsePath = ellipseToPath(canvasCx, canvasCy, canvasRx, canvasRy);
                paths.push({ dPath: ellipsePath, fillRule: 'nonzero' });

                // Update bounds
                globalMinX = Math.min(globalMinX, canvasCx - canvasRx);
                globalMinY = Math.min(globalMinY, canvasCy - canvasRy);
                globalMaxX = Math.max(globalMaxX, canvasCx + canvasRx);
                globalMaxY = Math.max(globalMaxY, canvasCy + canvasRy);
            }
        });

        if (paths.length === 0) {
            console.warn('[extractSvgPaths] No path, circle, or ellipse elements found in SVG');
            return null;
        }

        // Use calculated bounds or fall back to shape bounds
        const finalBounds = globalMinX !== Infinity ? {
            x: globalMinX,
            y: globalMinY,
            width: globalMaxX - globalMinX,
            height: globalMaxY - globalMinY
        } : {
            x: shapeX,
            y: shapeY,
            width: shapeWidth,
            height: shapeHeight
        };

        return { paths, bounds: finalBounds };
    } catch (error) {
        console.error('[extractSvgPaths] Error parsing SVG:', error);
        return null;
    }
}

/**
 * Create layer data from shapes
 */
function createLayerData(shapes: XcsShape[]): Record<string, XcsLayer> {
    const layers: Record<string, XcsLayer> = {};
    const layerNames: Record<string, string> = {
        [XCS_LAYER_BLUE]: 'Blue',
        [XCS_LAYER_RED]: 'Red',
        [XCS_LAYER_ORANGE]: 'Orange',
        [XCS_LAYER_YELLOW]: 'Yellow',
        [XCS_LAYER_GREEN]: 'Green',
        [XCS_LAYER_CYAN]: 'Cyan',
        [XCS_LAYER_PURPLE]: 'Purple',
        [XCS_LAYER_BLACK]: 'Black'
    };

    let order = 0;
    shapes.forEach(shape => {
        if (!layers[shape.layerTag]) {
            layers[shape.layerTag] = {
                name: layerNames[shape.layerTag] || `{Layer ${order + 1}}`,
                order: order++,
                visible: true
            };
        }
    });

    return layers;
}

// Shape processing info including optional process settings
interface ShapeProcessingInfo {
    processIgnore: boolean;
    processingType: 'VECTOR_ENGRAVING' | 'FILL_VECTOR_ENGRAVING' | 'VECTOR_CUTTING' | 'BITMAP_ENGRAVING';
    processData?: XcsProcessData;
}

/**
 * Create default VECTOR_CUTTING parameters
 */
function createDefaultVectorCuttingParams(lightSource: LightSourceMode): XcsVectorCuttingParams {
    return {
        processingLightSource: lightSource,
        power: 1,
        speed: 16,
        repeat: 1,
        enableKerf: false,
        kerfDistance: 0,
        enableBreakPoint: false,
        breakPointSize: 0.5,
        breakPointCount: 2,
        breakPointMode: 'count',
        breakPointDistance: 100,
        breakPointPower: 0,
        frequency: 40
    };
}

/**
 * Create default VECTOR_ENGRAVING parameters
 */
function createDefaultVectorEngravingParams(lightSource: LightSourceMode): XcsVectorEngravingParams {
    return {
        processingLightSource: lightSource,
        power: 1,
        speed: 20,
        repeat: 1,
        frequency: 40,
        enableKerf: false,
        kerfDistance: 0
    };
}

/**
 * Create default FILL_VECTOR_ENGRAVING parameters
 */
function createDefaultFillEngravingParams(lightSource: LightSourceMode): XcsFillEngravingParams {
    return {
        processingLightSource: lightSource,
        power: 1,
        speed: 80,
        repeat: 1,
        frequency: 40,
        bitmapEngraveMode: 'normal',
        density: 100,
        dotDuration: 10,
        dpi: 500,
        bitmapScanMode: 'zMode',
        scanAngle: 0,
        angleType: 2,
        crossAngle: false
    };
}

/**
 * Create default INTAGLIO parameters
 */
function createDefaultIntaglioParams(lightSource: LightSourceMode): XcsIntaglioParams {
    return {
        processingLightSource: lightSource,
        power: 1,
        speed: 80,
        repeat: 1,
        frequency: 40,
        bitmapEngraveMode: 'normal',
        density: 100,
        bitmapScanMode: 'zMode',
        sliceNumber: 100,
        processAngle: 15,
        zAxisMove: false,
        zLayers: 1,
        zDecline: 0.01
    };
}

/**
 * Convert ShapeProcessSettings to XcsProcessData
 * Creates full process data structure with all processing types
 * Custom settings override the defaults for the specific processing type
 */
function convertToXcsProcessData(
    scoreSettings: ShapeProcessSettings | undefined,
    engraveSettings: ShapeProcessSettings | undefined,
    cutSettings: ShapeProcessSettings | undefined,
    defaultLightSource: LightSourceMode
): XcsProcessData {
    // Per-operation light source (from settings) with fallback to default
    const scoreLightSource = scoreSettings?.lightSource || defaultLightSource;
    const engraveLightSource = engraveSettings?.lightSource || defaultLightSource;
    const cutLightSource = cutSettings?.lightSource || defaultLightSource;

    // Start with defaults using per-operation light sources
    const vectorCutting = createDefaultVectorCuttingParams(cutLightSource);
    const vectorEngraving = createDefaultVectorEngravingParams(scoreLightSource);
    const fillEngraving = createDefaultFillEngravingParams(engraveLightSource);
    const intaglio = createDefaultIntaglioParams(defaultLightSource);

    // Apply custom score settings (VECTOR_ENGRAVING)
    if (scoreSettings) {
        if (scoreSettings.power !== undefined) vectorEngraving.power = scoreSettings.power;
        if (scoreSettings.speed !== undefined) vectorEngraving.speed = scoreSettings.speed;
        if (scoreSettings.passes !== undefined) vectorEngraving.repeat = scoreSettings.passes;
        if (scoreSettings.kerfEnabled !== undefined) vectorEngraving.enableKerf = scoreSettings.kerfEnabled;
        if (scoreSettings.kerfDistance !== undefined) vectorEngraving.kerfDistance = scoreSettings.kerfDistance;
    }

    // Apply custom engrave settings (FILL_VECTOR_ENGRAVING)
    if (engraveSettings) {
        if (engraveSettings.power !== undefined) fillEngraving.power = engraveSettings.power;
        if (engraveSettings.speed !== undefined) fillEngraving.speed = engraveSettings.speed;
        if (engraveSettings.passes !== undefined) fillEngraving.repeat = engraveSettings.passes;
        if (engraveSettings.density !== undefined) fillEngraving.density = engraveSettings.density;
        if (engraveSettings.scanMode !== undefined) fillEngraving.bitmapScanMode = engraveSettings.scanMode;
        // Bitmap engrave settings for full round-trip fidelity
        if (engraveSettings.bitmapEngraveMode !== undefined) fillEngraving.bitmapEngraveMode = engraveSettings.bitmapEngraveMode;
        if (engraveSettings.dotDuration !== undefined) fillEngraving.dotDuration = engraveSettings.dotDuration;
    }

    // Apply custom cut settings (VECTOR_CUTTING)
    if (cutSettings) {
        if (cutSettings.power !== undefined) vectorCutting.power = cutSettings.power;
        if (cutSettings.speed !== undefined) vectorCutting.speed = cutSettings.speed;
        if (cutSettings.passes !== undefined) vectorCutting.repeat = cutSettings.passes;
        if (cutSettings.kerfEnabled !== undefined) vectorCutting.enableKerf = cutSettings.kerfEnabled;
        if (cutSettings.kerfDistance !== undefined) vectorCutting.kerfDistance = cutSettings.kerfDistance;
        // Break point / tab settings for full round-trip fidelity
        if (cutSettings.enableBreakPoint !== undefined) vectorCutting.enableBreakPoint = cutSettings.enableBreakPoint;
        if (cutSettings.breakPointSize !== undefined) vectorCutting.breakPointSize = cutSettings.breakPointSize;
        if (cutSettings.breakPointCount !== undefined) vectorCutting.breakPointCount = cutSettings.breakPointCount;
        if (cutSettings.breakPointMode !== undefined) vectorCutting.breakPointMode = cutSettings.breakPointMode as 'count' | 'distance';
        if (cutSettings.breakPointDistance !== undefined) vectorCutting.breakPointDistance = cutSettings.breakPointDistance;
        if (cutSettings.breakPointPower !== undefined) vectorCutting.breakPointPower = cutSettings.breakPointPower;
        // Note: lower focus (cuttingDrop) is handled at the machine level, not per-shape in XCS
    }

    return {
        VECTOR_CUTTING: {
            materialType: 'customize',
            planType: cutLightSource,
            parameter: { customize: vectorCutting }
        },
        VECTOR_ENGRAVING: {
            materialType: 'customize',
            planType: scoreLightSource,
            parameter: { customize: vectorEngraving }
        },
        FILL_VECTOR_ENGRAVING: {
            materialType: 'customize',
            planType: 'official',
            parameter: { customize: fillEngraving }
        },
        INTAGLIO: {
            materialType: 'customize',
            planType: defaultLightSource,
            parameter: { customize: intaglio }
        }
    };
}

/**
 * Create device data with processIgnore settings
 */
function createDeviceData(
    shapes: XcsShape[],
    shapeProcessingMap: Map<string, ShapeProcessingInfo>,
    laserConfig: LaserConfig,
    canvasId: string,
    lightSourceMode?: LightSourceMode
): XcsDeviceData {
    const lightSource = lightSourceMode || laserConfig.defaultLightSource || 'blue';

    // Create default process data for shapes without custom settings
    const defaultProcessData = convertToXcsProcessData(undefined, undefined, undefined, lightSource);

    const displays: [string, XcsShapeProcessingData][] = shapes.map(shape => {
        const processing = shapeProcessingMap.get(shape.id) || {
            processIgnore: false,
            processingType: 'FILL_VECTOR_ENGRAVING' as const
        };

        return [
            shape.id,
            {
                isFill: shape.isFill,
                type: shape.type,
                processingType: processing.processingType,
                data: processing.processData || defaultProcessData,
                processIgnore: processing.processIgnore
            }
        ];
    });

    // Build LASER_PLANE data matching xTool format
    const laserPlaneData: {
        material: number;
        lightSourceMode?: LightSourceMode;
        thickness: null;
        perimeter: null;
        diameter: null;
        isProcessByLayer: boolean;
        pathPlanning: string;
        fillPlanning: string;
        dreedyTsp: boolean;
        avoidSmokeModal: boolean;
        scanDirection: string;
        xcsUsed: unknown[];
    } = {
        material: 0,
        thickness: null,
        perimeter: null,
        diameter: null,
        isProcessByLayer: false,
        pathPlanning: 'auto',
        fillPlanning: 'separate',
        dreedyTsp: false,
        avoidSmokeModal: false,
        scanDirection: 'topToBottom',
        xcsUsed: []
    };

    // Add light source mode for supported lasers
    if (laserConfig.hasLightSourceMode) {
        laserPlaneData.lightSourceMode = lightSourceMode || laserConfig.defaultLightSource || 'blue';
    }

    return {
        id: laserConfig.extId,
        power: null,
        data: {
            dataType: 'Map',
            value: [[canvasId, {
                mode: 'LASER_PLANE',
                data: {
                    LASER_PLANE: laserPlaneData
                },
                displays: {
                    dataType: 'Map',
                    value: displays
                }
            }]]
        },
        materialList: [],
        materialTypeList: [],
        customProjectData: {}
    };
}

// ============================================================
// MAIN BUILD FUNCTION
// ============================================================

export interface XcsBuildOptions {
    laserType?: LaserType;
    fonts?: Font[];  // Database fonts for glyph extraction
    // Process settings per template shape, keyed by shape ID then operation
    processSettings?: Map<string, Map<string, ShapeProcessSettings>>;
    // Light source mode for multi-laser machines
    // F1: 'blue' (10W Diode) | 'red' (2W IR)
    // F1 Ultra / F2 Ultra: 'blue' (20W Diode) | 'fiber' (20W Fiber)
    // F2 Ultra UV: 'uv' (5W UV)
    lightSourceMode?: LightSourceMode;
}

/**
 * Build XCS file from template shapes and field values
 *
 * @param shapes - Template shapes from the database
 * @param fields - Extended field definitions with score/engrave/fontFamily
 * @param values - User-entered values keyed by fieldId
 * @param width - Canvas width in mm
 * @param height - Canvas height in mm
 * @param options - Optional settings (laser type, fonts, etc.)
 * @returns XCS file content as JSON string
 */
export async function buildXcs(
    shapes: TemplateShape[],
    fields: ExtendedField[],
    values: Record<string, string>,
    _width: number,  // Canvas width (reserved for future use)
    _height: number, // Canvas height (reserved for future use)
    options: XcsBuildOptions = {}
): Promise<string> {
    const laserType = options.laserType || 'diode';
    const laserConfig = LASER_CONFIGS[laserType];
    const lightSource = options.lightSourceMode || laserConfig.defaultLightSource || 'blue';

    const xcsShapes: XcsShape[] = [];
    const shapeProcessingMap = new Map<string, ShapeProcessingInfo>();
    const processSettings = options.processSettings;

    // Sort shapes by orderIndex to preserve layer order (lower index = front layer)
    // This ensures exported XCS maintains the same visual stacking as the original
    const sortedShapes = [...shapes].sort((a, b) => {
        return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    });

    // Calculate zOrder values: front layer (first in array) gets highest zOrder
    // This matches xTool's convention where higher zOrder = front layer
    // Use originalZOrder if available for perfect round-trip, otherwise calculate
    let zOrderCounter = 0;
    const getZOrder = (shape: TemplateShape): number => {
        const originalZOrder = shape.originalXcsData?.originalZOrder;
        if (originalZOrder !== undefined) {
            return originalZOrder;
        }
        // For new shapes, calculate descending zOrder based on position
        // Higher zOrder for earlier shapes (front layer)
        return sortedShapes.length * 3 - zOrderCounter++;
    };

    // Helper to get processIgnore from process settings
    // outputEnabled=true → processIgnore=false (will be processed)
    // outputEnabled=false → processIgnore=true (will be skipped)
    const getProcessIgnore = (shapeId: string, operation: 'score' | 'engrave' | 'cut'): boolean => {
        const shapeSettings = processSettings?.get(shapeId);
        const opSettings = shapeSettings?.get(operation);
        // Default to false (outputEnabled defaults to true, so processIgnore defaults to false)
        return opSettings?.outputEnabled === false;
    };

    for (const shape of sortedShapes) {
        // Handle graphic shapes (SVG monograms, decorative elements)
        if (shape.type === 'graphic' && shape.svgData) {
            // Check score/engrave/cut flags (default: engrave only)
            const useScore = shape.score ?? false;
            const useEngrave = shape.engrave ?? true;
            const useCut = shape.cut ?? false;

            // Get custom colors (with defaults)
            const scoreColor = shape.scoreColor || XCS_LAYER_RED;
            const engraveColor = shape.engraveColor || XCS_LAYER_BLACK;
            const cutColor = shape.cutColor || XCS_LAYER_CYAN;

            // Get process settings for this shape
            const shapeSettings = processSettings?.get(shape.id);
            const shapeProcessData = convertToXcsProcessData(
                shapeSettings?.get('score'),
                shapeSettings?.get('engrave'),
                shapeSettings?.get('cut'),
                lightSource
            );

            // Check for preserved XCS data (for lossless round-trip)
            const xcsData = shape.originalXcsData;
            if (xcsData) {
                // Handle TEXT shapes with preserved glyph data
                if (xcsData.type === 'TEXT' && xcsData.text && xcsData.fontData?.glyphData) {
                    const layerTag = useEngrave ? engraveColor : (useScore ? scoreColor : cutColor);
                    const isFill = useEngrave;

                    // Build originalPosition if we have preserved XCS positioning values
                    const originalPosition = (xcsData.originalX !== undefined && xcsData.originalScale)
                        ? {
                            x: xcsData.originalX,
                            y: xcsData.originalY!,
                            width: xcsData.originalWidth!,
                            height: xcsData.originalHeight!,
                            scale: xcsData.originalScale,
                        }
                        : undefined;

                    // Create TEXT shape with preserved glyph data and positioning
                    const textShape = createXcsText(
                        xcsData.text,
                        shape.x,
                        shape.y,
                        shape.width,
                        shape.height,
                        xcsData.fontSize || 12,
                        xcsData.fontFamily || 'Arial',
                        layerTag,
                        isFill,
                        getZOrder(shape),
                        {
                            fontInfo: {
                                unitsPerEm: xcsData.fontData.fontInfo?.unitsPerEm || 1000,
                                lineHeight: xcsData.fontData.fontInfo?.lineHeight || 72,
                                ascent: xcsData.fontData.fontInfo?.ascent || 60,
                                descent: xcsData.fontData.fontInfo?.descent || -12,
                                capHeight: 50,
                                xHeight: 40,
                                lineGap: 0,
                            },
                            glyphData: xcsData.fontData.glyphData as Record<string, {
                                dPath: string;
                                advanceWidth: number;
                                advanceHeight: number;
                                leftBearing: number;
                                topBearing: number;
                                bbox: { minX: number; minY: number; maxX: number; maxY: number };
                            }>,
                        },
                        originalPosition  // Pass preserved positioning for exact round-trip
                    );
                    xcsShapes.push(textShape);
                    // Determine which operation this text shape uses
                    const textOperation: 'score' | 'engrave' | 'cut' = useEngrave ? 'engrave' : (useScore ? 'score' : 'cut');
                    shapeProcessingMap.set(textShape.id, {
                        processIgnore: getProcessIgnore(shape.id, textOperation),
                        processingType: isFill ? 'FILL_VECTOR_ENGRAVING' : 'VECTOR_ENGRAVING',
                        processData: shapeProcessData
                    });
                    continue;
                }

                // For PATH, STAR, POLYGON: fall through to SVG extraction
                // These have embedded transforms that extractSvgPaths handles correctly
            }

            // Fallback: Extract paths from SVG (for shapes without originalXcsData)
            const extracted = extractSvgPaths(
                shape.svgData,
                shape.x,
                shape.y,
                shape.width,
                shape.height
            );

            if (extracted && extracted.paths.length > 0) {
                // Determine if this is a compound path (multiple paths that should be merged)
                const isCompound = extracted.paths.length > 1;
                const mergedDPath = isCompound ? extracted.paths.map(p => p.dPath).join(' ') : extracted.paths[0].dPath;
                const fillRule = isCompound
                    ? (extracted.paths.some(p => p.fillRule === 'evenodd') ? 'evenodd' : 'nonzero')
                    : extracted.paths[0].fillRule;

                // Always use extracted bounds for positioning
                // SVG extraction handles embedded transforms correctly
                const boundsX = extracted.bounds.x;
                const boundsY = extracted.bounds.y;
                const boundsW = extracted.bounds.width;
                const boundsH = extracted.bounds.height;

                // Create score path (stroke only) if enabled
                if (useScore) {
                    const scorePathShape = createXcsPath(
                        mergedDPath,
                        boundsX,
                        boundsY,
                        boundsW,
                        boundsH,
                        scoreColor,
                        false,
                        getZOrder(shape),
                        isCompound,
                        fillRule
                    );
                    xcsShapes.push(scorePathShape);
                    shapeProcessingMap.set(scorePathShape.id, {
                        processIgnore: getProcessIgnore(shape.id, 'score'),
                        processingType: 'VECTOR_ENGRAVING',
                        processData: shapeProcessData
                    });
                }

                // Create engrave path (filled) if enabled
                if (useEngrave) {
                    const engravePathShape = createXcsPath(
                        mergedDPath,
                        boundsX,
                        boundsY,
                        boundsW,
                        boundsH,
                        engraveColor,
                        true,
                        getZOrder(shape),
                        isCompound,
                        fillRule
                    );
                    xcsShapes.push(engravePathShape);
                    shapeProcessingMap.set(engravePathShape.id, {
                        processIgnore: getProcessIgnore(shape.id, 'engrave'),
                        processingType: 'FILL_VECTOR_ENGRAVING',
                        processData: shapeProcessData
                    });
                }

                // Create cut path (stroke, cut through material) if enabled
                if (useCut) {
                    const cutPathShape = createXcsPath(
                        mergedDPath,
                        boundsX,
                        boundsY,
                        boundsW,
                        boundsH,
                        cutColor,
                        false,
                        getZOrder(shape),
                        isCompound,
                        fillRule
                    );
                    xcsShapes.push(cutPathShape);
                    shapeProcessingMap.set(cutPathShape.id, {
                        processIgnore: getProcessIgnore(shape.id, 'cut'),
                        processingType: 'VECTOR_CUTTING',
                        processData: shapeProcessData
                    });
                }

                // Log warning if none are enabled
                if (!useScore && !useEngrave && !useCut) {
                    console.warn(`[buildXcs] Graphic shape ${shape.id} has no score, engrave, or cut enabled - skipping`);
                }
            } else {
                console.warn(`[buildXcs] Could not extract paths from graphic shape: ${shape.id}`);
            }

            continue;
        }

        // Handle raster shapes (BITMAP - PNG, JPG, BMP images)
        if (shape.type === 'raster' && shape.rasterData) {
            // Rasters only support engrave - check if it's enabled
            const useEngrave = shape.engrave !== false;

            if (!useEngrave) {
                console.warn(`[buildXcs] Raster shape ${shape.id} has engrave disabled - skipping`);
                continue;
            }

            // Check for preserved XCS data (for lossless round-trip)
            const xcsData = shape.originalXcsData;

            // Get layer tag from preserved data or default to black
            const layerTag = xcsData?.layerTag || shape.engraveColor || XCS_LAYER_BLACK;

            // Create BITMAP shape with original dimensions for proper scaling
            const bitmapShape = createXcsBitmap(
                shape.rasterData,  // Already has data:image/... prefix
                shape.x,
                shape.y,
                shape.width,
                shape.height,
                shape.rotation || 0,
                xcsData?.originalZOrder ?? getZOrder(shape),
                layerTag,
                shape.rasterOriginalWidth ?? undefined,
                shape.rasterOriginalHeight ?? undefined
            );

            // Preserve additional XCS properties if available (for round-trip)
            if (xcsData?.visible !== undefined) {
                bitmapShape.visible = xcsData.visible;
            }
            if (xcsData?.lock !== undefined) {
                bitmapShape.lockState = xcsData.lock;
            }

            xcsShapes.push(bitmapShape);

            // Get process settings for this raster shape
            const shapeSettings = processSettings?.get(shape.id);
            const engraveSettings = shapeSettings?.get('engrave');
            const rasterLightSource = engraveSettings?.lightSource || lightSource;

            // BITMAP uses BITMAP_ENGRAVING processing type with full parameter structure
            shapeProcessingMap.set(bitmapShape.id, {
                processIgnore: getProcessIgnore(shape.id, 'engrave'),
                processingType: 'BITMAP_ENGRAVING',
                processData: {
                    BITMAP_ENGRAVING: {
                        materialType: 'customize',
                        planType: rasterLightSource,
                        parameter: {
                            customize: {
                                dotDuration: engraveSettings?.dotDuration ?? 100,
                                power: engraveSettings?.power ?? 10,
                                dpi: engraveSettings?.density ?? 500,
                                powerMinMaxRange: [1, 100],
                                speed: engraveSettings?.speed ?? 80,
                                repeat: engraveSettings?.passes ?? 1,
                                bitmapMode: 'grayscale',
                                bitmapScanMode: engraveSettings?.scanMode ?? 'zMode',
                                bitmapEngraveMode: 'dot',  // BITMAP_ENGRAVING uses 'dot'|'line', not 'normal'|'reverse'
                                processingLightSource: rasterLightSource
                            }
                        }
                    }
                }
            });

            continue;
        }

        // Handle rectangle shapes (text zones)
        if (shape.type === 'rect') {
            // Add the rectangle itself (blue layer, processIgnore: true)
            const rectShape = createXcsRect(
                shape.x,
                shape.y,
                shape.width,
                shape.height,
                XCS_LAYER_BLUE,
                getZOrder(shape)
            );
            xcsShapes.push(rectShape);
            shapeProcessingMap.set(rectShape.id, {
                processIgnore: true,
                processingType: 'VECTOR_ENGRAVING'
            });

            // If field is assigned and has a value, add text shapes
            const field = fields.find(f => f.id === shape.fieldId);
            if (field && shape.fieldId) {
                const value = values[shape.fieldId] || '';
                if (value) {
                    const fontFamily = field.fontFamily || DEFAULT_FONT_FAMILY;

                    // Convert fonts to textGenerator format for glyph-based measurement
                    const fonts = options.fonts || [];
                    const textGenFonts: TextGenFont[] = fonts
                        .filter(f => f.url)
                        .map(f => ({
                            id: f.id,
                            name: f.name,
                            family: f.family,
                            url: f.url!
                        }));

                    // Calculate fontSize using glyph-based measurement
                    // This returns the exact fontSize to fit text in the box
                    const fontSize = await calculateFontSizeForBox(
                        value,
                        fontFamily,
                        textGenFonts,
                        { x: shape.x, y: shape.y, width: shape.width, height: shape.height },
                        ROBOTO_REGULAR_BASE64  // Fallback font
                    );

                    // Get laser operations from SHAPE settings (not field)
                    const useScore = shape.score ?? false;
                    const useEngrave = shape.engrave !== false;  // Default true if undefined
                    const useCut = shape.cut ?? false;

                    // Get custom colors from shape (with defaults)
                    const scoreColor = shape.scoreColor || XCS_LAYER_RED;
                    const engraveColor = shape.engraveColor || XCS_LAYER_BLACK;
                    const cutColor = shape.cutColor || XCS_LAYER_CYAN;

                    // Get process settings for this shape and create unified process data
                    const shapeSettings = processSettings?.get(shape.id);
                    const textProcessData = convertToXcsProcessData(
                        shapeSettings?.get('score'),
                        shapeSettings?.get('engrave'),
                        shapeSettings?.get('cut'),
                        lightSource
                    );

                    // Extract glyph data for this text
                    const fontData = await extractFontData(value, fontFamily, fontSize, fonts);

                    // Score text (stroke only)
                    if (useScore) {
                        const scoreText = createXcsText(
                            value,
                            shape.x,  // Use shape bounding box (centering via offsetX)
                            shape.y,
                            shape.width,  // Use shape width
                            shape.height,
                            fontSize,
                            fontFamily,
                            scoreColor,  // Custom score color from shape
                            false,
                            getZOrder(shape),
                            fontData
                        );
                        xcsShapes.push(scoreText);
                        shapeProcessingMap.set(scoreText.id, {
                            processIgnore: getProcessIgnore(shape.id, 'score'),
                            processingType: 'VECTOR_ENGRAVING',
                            processData: textProcessData
                        });
                    }

                    // Engrave text (filled)
                    if (useEngrave) {
                        const engraveText = createXcsText(
                            value,
                            shape.x,  // Use shape bounding box (centering via offsetX)
                            shape.y,
                            shape.width,  // Use shape width
                            shape.height,
                            fontSize,
                            fontFamily,
                            engraveColor,  // Custom engrave color from shape
                            true,
                            getZOrder(shape),
                            fontData
                        );
                        xcsShapes.push(engraveText);
                        shapeProcessingMap.set(engraveText.id, {
                            processIgnore: getProcessIgnore(shape.id, 'engrave'),
                            processingType: 'FILL_VECTOR_ENGRAVING',
                            processData: textProcessData
                        });
                    }

                    // Cut text (stroke, cuts through material)
                    if (useCut) {
                        const cutText = createXcsText(
                            value,
                            shape.x,  // Use shape bounding box (centering via offsetX)
                            shape.y,
                            shape.width,  // Use shape width
                            shape.height,
                            fontSize,
                            fontFamily,
                            cutColor,  // Custom cut color from shape
                            false,  // isFill = false for stroke (cutting is outline-based)
                            getZOrder(shape),
                            fontData
                        );
                        xcsShapes.push(cutText);
                        shapeProcessingMap.set(cutText.id, {
                            processIgnore: getProcessIgnore(shape.id, 'cut'),
                            processingType: 'VECTOR_CUTTING',
                            processData: textProcessData
                        });
                    }
                }
            }
        }
    }

    // Create canvas panel (canvasId and panel id are the same in xTool format)
    const canvasId = generateUuid();
    const now = Date.now();

    const canvas: XcsCanvasPanel = {
        id: canvasId,
        title: '{panel}1',
        layerData: createLayerData(xcsShapes),
        groupData: {},
        displays: xcsShapes
    };

    // Create XCS file structure
    const xcsFile: XcsFile = {
        canvasId,
        canvas: [canvas],
        extId: laserConfig.extId,
        extName: laserConfig.extName,
        device: createDeviceData(xcsShapes, shapeProcessingMap, laserConfig, canvasId, options.lightSourceMode),
        version: '1.0.17',
        created: now,
        modify: now,
        ua: 'LaserKiosk/1.0',
        meta: [{
            version: '1.0.17',
            date: now,
            ua: 'LaserKiosk/1.0'
        }],
        cover: '',
        minRequiredVersion: '3.0.0',
        appMinRequiredVersion: '',
        webMinRequiredVersion: ''
    };

    return JSON.stringify(xcsFile, null, 2);
}
